import { RealtimeClient } from '@batiflow/client-core';
import { useSession } from '@batiflow/client-core/react';
import type { PresenceStatus } from '@batiflow/contracts';
import { useEffect, useMemo, type ReactNode } from 'react';
import {
  approvalFragments,
  documentFragments,
  invalidateEinvoice,
  invalidateFragments,
  NOTIFICATION_FRAGMENTS,
  taskFragments,
} from '@/features/realtime/cache';
import { RealtimeContext } from '@/features/realtime/context';
import { RoomRegistry } from '@/features/realtime/rooms';
import { selectEffectiveStatus } from '@/features/realtime/store';
import { useIdleAway } from '@/features/realtime/use-idle-away';
import { useRuntime } from './runtime-provider';

/**
 * Realtime (docs/plan/12-frontend.md §5): one socket.io connection (client-core) per signed-in
 * session, opened once the session is ready (signed in, second factor satisfied) and closed when
 * it ends. Screens subscribe rooms with `useRealtimeRoom`. Events only mark cached queries as
 * stale (REST is the truth) or update the presence store; the events that move the user
 * (`access.revoked`) are handled by `RealtimeRouteEffects`, which lives inside the router.
 */
export function RealtimeProvider({ children }: { children?: ReactNode }) {
  const { queryClient, realtimeStore, realtime, redirectToLogin } = useRuntime();
  const status = useSession((s) => s.status);
  const mfaRequired = useSession((s) => s.mfaRequired);
  const ready = realtime.enabled && status === 'authenticated' && !mfaRequired;
  const registry = useMemo(() => new RoomRegistry(), []);
  const { socketFactory } = realtime;

  useIdleAway(realtimeStore, ready);

  // The client exists as soon as the session is ready, so screens can subscribe their rooms at
  // once (they are joined when the connection opens); the effect below opens and closes it.
  const session = useMemo(() => {
    if (!ready) return null;
    // The status the server holds for this connection (it starts as "available").
    let sent: PresenceStatus = 'available';
    // How many times the status message was set when the server last got it (0: never sent).
    let sentRevision = 0;
    const holder: { client?: RealtimeClient } = {};
    const flushPresence = (fresh = false) => {
      if (fresh) {
        sent = 'available';
        sentRevision = 0;
      }
      const state = realtimeStore.getState();
      const wanted = selectEffectiveStatus(state);
      const messageChanged = state.messageRevision !== sentRevision;
      if (wanted === sent && !messageChanged) return;
      // The message is sent only when it was set; omitted, the server keeps the current one.
      const request = messageChanged
        ? { status: wanted, message: state.ownMessage }
        : { status: wanted };
      if (holder.client?.send('presence.set', request)) {
        sent = wanted;
        sentRevision = state.messageRevision;
      }
    };
    const client = new RealtimeClient({
      ...(socketFactory ? { socketFactory } : {}),
      // After a reconnection events may have been missed: refetch what the open rooms show, and
      // the notifications.
      onReconnect: (rooms) => {
        void invalidateFragments(queryClient, [
          ...registry.fragments(rooms),
          ...NOTIFICATION_FRAGMENTS,
        ]);
      },
      onConnectionChange: (connected) => {
        realtimeStore.getState().setLink(connected ? 'up' : 'down');
        if (connected) flushPresence(true);
      },
    });
    holder.client = client;
    return { client, flushPresence };
  }, [ready, socketFactory, queryClient, realtimeStore, registry]);

  useEffect(() => {
    if (!session) return;
    const { client, flushPresence } = session;
    const off = [
      client.on('notification.created', () => {
        void invalidateFragments(queryClient, NOTIFICATION_FRAGMENTS);
      }),
      client.on('document.version.status_changed', ({ data }) => {
        void invalidateFragments(queryClient, documentFragments(data.spaceId));
      }),
      client.on('document.updated', ({ data }) => {
        void invalidateFragments(queryClient, documentFragments(data.spaceId));
      }),
      client.on('approval.updated', ({ data }) => {
        void invalidateFragments(queryClient, approvalFragments(data.spaceId));
      }),
      client.on('task.updated', ({ data }) => {
        void invalidateFragments(queryClient, taskFragments(data.projectId, data.taskId));
      }),
      // El Fatoora: the invoice page's status chip and panel refetch (REST is the truth).
      client.on('einvoice.status_changed', ({ data }) => {
        void invalidateEinvoice(queryClient, data.organizationId, data.invoiceId);
      }),
      client.on('presence.snapshot', ({ data }) => {
        realtimeStore.getState().applySnapshot(data.items);
      }),
      client.on('presence.changed', ({ data }) => {
        realtimeStore.getState().applyChange(data.userId, data.status, data.message);
      }),
      // The session was ended elsewhere or by an administrator: same path as a 401.
      client.on('session.revoked', () => {
        redirectToLogin();
      }),
      realtimeStore.subscribe(() => {
        flushPresence();
      }),
    ];
    client.connect();
    return () => {
      for (const remove of off) remove();
      client.disconnect();
      realtimeStore.getState().reset();
    };
  }, [session, queryClient, realtimeStore, redirectToLogin]);

  const value = useMemo(() => ({ client: session?.client ?? null, registry }), [session, registry]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
